import { useCallback, useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import client from './contentfulClientToken';
import { getDominantColor } from './utils/colorExtractor';
import './App.scss';


window.addEventListener(
    'contextmenu',
    function (e) {
        e.preventDefault();
    },
    false
);

// Convert RGB to HSL for sorting
function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max - min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            default:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
}

// Parse RGB string to object
function parseRgbString(rgbString) {
    const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return { r: 128, g: 128, b: 128 };
    return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10)
    };
}











// Removed old complex color extraction functions

// Simplified color extraction using colorExtractor utility with caching
async function extractColorFromUrl(srcUrl, name) {
    try {

        // Load image to get dimensions
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = srcUrl;
        });

        // Get dominant color using the utility
        const colorString = await getDominantColor(srcUrl, 5);
        const rgb = parseRgbString(colorString);
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

        let displayUrl = srcUrl;

        const result = {
            url: srcUrl,
            width: image.width,
            height: image.height,
            rgb,
            hsl,
            displayUrl,
            name: name || srcUrl,
        };


        return result;
    } catch (e) {
        throw e;
    }
}







function App() {
    const [items, setItems] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [columns, setColumns] = useState(10);
    const [isMobile, setIsMobile] = useState(false);
    const [shownCount, setShownCount] = useState(0);
    const shownIdsRef = useRef(new Set());
    // Track which images have their overlay toggled on
    const [toggledIds, setToggledIds] = useState(() => new Set());
    const [loaderFadingOut, setLoaderFadingOut] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

    const gridRef = useRef(null);
    const isTouchDraggingRef = useRef(false);
    const toggledInCurrentTouchRef = useRef(new Set());
    const lastTouchTimeRef = useRef(0);
    const isTouchRef = useRef(false);


    // Proactively unregister any previously installed service workers
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations()
                .then((regs) => regs.forEach((reg) => reg.unregister()))
                .catch(() => { });
        }
    }, []);

    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // With toolbar at the bottom, we don't need top offset; keep it zero
    useEffect(() => {
        document.documentElement.style.setProperty('--toolbar-height', `0px`);
    }, []);

    // Track mobile viewport to adjust column bounds
    useEffect(() => {
        const mql = window.matchMedia('(max-width: 768px)');
        const update = () => setIsMobile(Boolean(mql.matches));
        update();
        try {
            mql.addEventListener('change', update);
        } catch (_) {
            // Safari
            mql.addListener(update);
        }
        return () => {
            try {
                mql.removeEventListener('change', update);
            } catch (_) {
                mql.removeListener(update);
            }
        };
    }, []);

    const minColumns = isMobile ? 1 : 3;
    const maxColumns = isMobile ? 5 : 10;

    // Clamp columns when bounds change
    useEffect(() => {
        setColumns((c) => Math.max(minColumns, Math.min(maxColumns, c)));
    }, [minColumns, maxColumns]);

    // removed stacked overlay logic

    const sortItemsByMode = useCallback((list) => {
        const copy = [...list];
        copy.sort((a, b) => {
            const aHsl = a.hsl;
            const bHsl = b.hsl;

            // Sort by hue (0-360), then saturation, then lightness
            const aH = aHsl.h ?? 0;
            const bH = bHsl.h ?? 0;
            if (aH !== bH) return aH - bH;

            const aS = aHsl.s ?? 0;
            const bS = bHsl.s ?? 0;
            if (aS !== bS) return bS - aS; // higher saturation first

            const aL = aHsl.l ?? 0;
            const bL = bHsl.l ?? 0;
            return aL - bL; // darker first
        });
        return copy;
    }, []);

    // removed manual file input loader and animation logic

    // Auto-load images from Contentful on mount (from a specific entry id)
    useEffect(() => {
        let revoked = [];
        let cancelled = false;
        const load = async () => {
            try {
                setIsProcessing(true);
                setErrorMessage('');
                setItems([]); // Clear previous items

                const entryId = '4AzofV8yAC1aGBqtBTIcUC';
                const entry = await client.getEntry(entryId, { include: 10 });

                const assets = [];
                const visited = new Set();
                const walk = (val) => {
                    if (!val) return;
                    if (Array.isArray(val)) {
                        val.forEach(walk);
                        return;
                    }
                    if (val?.sys?.type === 'Asset' && val?.fields?.file && !visited.has(val.sys.id)) {
                        visited.add(val.sys.id);
                        assets.push(val);
                        return;
                    }
                    if (val?.fields && typeof val.fields === 'object') {
                        Object.values(val.fields).forEach(walk);
                    }
                };
                walk(entry);

                let index = 0;
                const revokedLocal = [];
                let loadedItems = [];

                for (const asset of assets) {
                    if (cancelled) break;
                    try {
                        const file = asset?.fields?.file;
                        const rawUrl = file?.url ? (file.url.startsWith('http') ? file.url : `https:${file.url}`) : null;
                        if (!rawUrl) continue;
                        const targetH = isMobile ? 750 : 750;
                        const url = rawUrl.includes('?') ? `${rawUrl}&h=${targetH}` : `${rawUrl}?h=${targetH}`;
                        const name = asset?.fields?.title || asset?.sys?.id;

                        const meta = await extractColorFromUrl(url, name);

                        const item = {
                            id: `${meta.name}-${meta.width}-${meta.height}-${index}`,
                            name: meta.name,
                            fileSize: 0,
                            appearIndex: index,
                            appearDelayMs: index * 100,
                            ...meta,
                        };
                        index += 1;
                        loadedItems = [...loadedItems, item];
                        if (meta.displayUrl && !String(meta.displayUrl).startsWith('http')) {
                            revokedLocal.push(meta.displayUrl);
                        }
                        // Sort and update UI incrementally as each image is processed
                        const sorted = sortItemsByMode([...loadedItems])
                            .map((it, i) => ({ ...it, appearDelayMs: i * 100 }));
                        setItems(sorted);
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.warn('Failed to load asset', err);
                        continue;
                    }
                }
                revoked = revokedLocal;
            } catch (e) {
                setErrorMessage('Failed to load images from Contentful entry.');
                // eslint-disable-next-line no-console
                console.error(e);
            } finally {
                setIsProcessing(false);
            }
        };
        load();
        return () => {
            cancelled = true;
            revoked.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) { } });
        };
    }, [sortItemsByMode, isMobile]);

    // Hover toggles overlay; implemented via JS to persist state
    const handleGridItemAnimationEnd = useCallback((id, e) => {
        if (!e || e.animationName !== 'fadeIn') return;
        const setRef = shownIdsRef.current;
        if (!setRef.has(id)) {
            setRef.add(id);
            setShownCount(setRef.size);
        }
    }, []);

    const toggleImage = useCallback((id) => {
        setToggledIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const processTouchPoint = useCallback((x, y) => {
        let el = document.elementFromPoint(x, y);
        while (el && !el.dataset?.id) {
            el = el.parentElement;
        }
        const id = el?.dataset?.id;
        if (!id) return;
        if (!toggledInCurrentTouchRef.current.has(id)) {
            toggledInCurrentTouchRef.current.add(id);
            toggleImage(id);
        }
    }, [toggleImage]);

    const handleTouchStart = useCallback((e) => {
        isTouchRef.current = true;
        isTouchDraggingRef.current = true;
        toggledInCurrentTouchRef.current = new Set();
        lastTouchTimeRef.current = performance.now();
        const t = e.touches && e.touches[0];
        if (t) processTouchPoint(t.clientX, t.clientY);
    }, [processTouchPoint]);

    const handleTouchMove = useCallback((e) => {
        if (!isTouchDraggingRef.current) return;
        const t = e.touches && e.touches[0];
        if (t) processTouchPoint(t.clientX, t.clientY);
    }, [processTouchPoint]);

    const handleTouchEnd = useCallback(() => {
        isTouchDraggingRef.current = false;
        toggledInCurrentTouchRef.current = new Set();
    }, []);

    const handleImageClick = useCallback((id) => {
        if (performance.now() - lastTouchTimeRef.current < 500) return;
        toggleImage(id);
    }, [toggleImage]);

    const handleImageMouseEnter = useCallback((item) => {
        setHoveredItem(item);
    }, []);

    const handleImageMouseLeave = useCallback(() => {
        setHoveredItem(null);
    }, []);

    const handleMouseMove = useCallback((e) => {
        setCursorPos({ x: e.clientX, y: e.clientY });
    }, []);

    // Trigger fade out when all images are shown
    useEffect(() => {
        if (items.length > 0 && shownCount >= items.length && !loaderFadingOut) {
            setLoaderFadingOut(true);
        }
    }, [items.length, shownCount, loaderFadingOut]);



    // removed lightbox feature

    // Removed drag-and-drop; keep only button upload

    return (
        <div className="App" onMouseMove={handleMouseMove}>
            {(isProcessing || (items.length > 0 && shownCount < items.length) || loaderFadingOut) && (
                <div
                    className={`initial-loading${loaderFadingOut ? ' fade-out' : ''}`}
                    aria-live="assertive"
                    aria-busy="true"
                >
                    ind*hexing
                </div>
            )}
            <div className="toolbar">
                <div className="left">
                    {/* Controls moved to floating bottom-right */}
                </div>
                <div className="right">
                    <span className="button small" aria-live="polite">
                        {isProcessing ? 'Loading…' : `${items.length} images`}
                    </span>
                </div>
            </div>
            {/* Floating controls bottom (appear when images are present) */}
            {items.length > 0 && (
                <div className="floating-controls">
                    <div
                        className="floating-control-left"
                        onClick={() => setColumns((c) => Math.min(maxColumns, c + 1))}
                        aria-label="Increase columns"
                        title="Increase columns"
                        role="button"
                        tabIndex={0}
                    >
                        minus
                    </div>
                    <div
                        className="floating-control-right"
                        onClick={() => setColumns((c) => Math.max(minColumns, c - 1))}
                        aria-label="Decrease columns"
                        title="Decrease columns"
                        role="button"
                        tabIndex={0}
                    >
                        plus
                    </div>
                </div>
            )}
            {errorMessage && <div className="error">{errorMessage}</div>}

            {items.length > 0 && (
                <>


                    <div ref={gridRef} className="grid" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchEnd} style={{ gridTemplateColumns: `repeat(${Math.max(minColumns, Math.min(maxColumns, columns))}, 1fr)` }}>
                        {items.map((item) => (
                            <div
                                className="grid-item"
                                key={item.id}
                                data-id={item.id}
                                style={{ animationDelay: `${item.appearDelayMs || 0}ms` }}
                                onAnimationEnd={(e) => handleGridItemAnimationEnd(item.id, e)}
                            >
                                <div
                                    className={`image${toggledIds.has(item.id) ? ' toggled' : ''}`}
                                    onMouseEnter={() => {
                                        if (isTouchRef.current) return;
                                        toggleImage(item.id);
                                        handleImageMouseEnter(item);
                                    }}
                                    onMouseLeave={handleImageMouseLeave}
                                    onClick={() => handleImageClick(item.id)}
                                    style={{
                                        backgroundColor: `rgb(${item.rgb.r}, ${item.rgb.g}, ${item.rgb.b})`,
                                    }}
                                >
                                    <div className="overlay-hex"></div>
                                    <div
                                        className="swatch"
                                    /*   style={{
                                          backgroundColor: `rgb(${(item.dominantRgb || item.averageRgb).r}, ${(item.dominantRgb || item.averageRgb).g}, ${(item.dominantRgb || item.averageRgb).b})`,
                                      }}
                                      title={`Hue: ${((item.dominantHsl || item.averageHsl).h).toFixed(0)}°`} */
                                    >
                                        <span
                                            className="date-in-swatch"
                                            style={{
                                                color: (() => {
                                                    const base = item.hsl || { h: 0, s: 0, l: 50 };
                                                    const delta = 35;
                                                    const targetL = base.l > 50 ? (base.l - delta) : (base.l + delta);
                                                    const l = Math.max(0, Math.min(100, Math.round(targetL)));
                                                    const s = base.s < 8 ? 0 : Math.max(0, Math.min(100, Math.round(base.s)));
                                                    const h = Math.max(0, Math.min(360, Math.round(base.h)));
                                                    return `hsl(${h}, ${s}%, ${l}%)`;
                                                })()
                                            }}
                                        >
                                            {(() => {
                                                const rgb = item.rgb;
                                                if (!rgb) return '';
                                                const hex = `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1).toUpperCase()}`;
                                                return hex;
                                            })()}
                                        </span>
                                    </div>
                                    <img
                                        src={item.displayUrl || item.url}
                                        alt=""
                                        loading="lazy"
                                        width={item.width}
                                        height={item.height}
                                    />
                                </div>
                                {/*  <div className="meta">
                <span className="name" title={item.takenAtLabel}>{item.takenAtLabel}</span>
                <span className="hue">{Math.round((item.dominantHsl || item.averageHsl).h)}°</span>
              </div> */}
                            </div>
                        ))}
                    </div>
                    {/* Bottom-left live counter */}
                    {shownCount > 0 && (
                        <div className="bottom-left-counter" aria-live="polite">
                            {shownCount}
                        </div>


                    )}
                    <div className="about">
                        <p>a collection of contemporary graphics, ranked by hue.</p>
                    </div>
                </>
            )}
            {/* Custom hex cursor */}
            {hoveredItem && (
                <div
                    className="hex-cursor"
                    style={{
                        left: `${cursorPos.x}px`,
                        top: `${cursorPos.y}px`,
                    }}
                >
                    {(() => {
                        const rgb = hoveredItem.rgb;
                        if (!rgb) return '';
                        const hex = `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1).toUpperCase()}`;
                        return hex;
                    })()}
                </div>
            )}
            {/*   {hoveredItem && (
                <div className="hover-hex-fixed" aria-live="polite">
                    <p>{(() => {
                        const rgb = hoveredItem.rgb;
                        if (!rgb) return '';
                        const hex = `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1).toUpperCase()}`;
                        return hex;
                    })()}</p>
                </div>
            )} */}
        </div>
    );
}

export default App;
